import { usePageModels } from '@/context/PageModels';
import { usePartnerOnly } from '@/hooks';
import { IS_PREVIEW, Theme } from '@/utils/constants';
import {
  reduceCheckboxDefault,
  reducePublicationDateCheckbox,
} from '@/utils/reducers/reduceFilter';
import { Typography } from '@material-ui/core';
import { intersection, orderBy } from 'lodash';
import { find, get, map, pipe } from 'lodash/fp';
import React from 'react';
import useDeepCompareEffect from 'use-deep-compare-effect';
import Button from '../Button';
import ElasticComponentContext from '../ElasticComponent/ElasticComponentContext';
import { useProcessFacets } from '../Hooks/facets/useProcessFacets';
import { MuiFormControlLabel, MuiFormGroup } from '../material-ui';
import PartnerOnly from '../PartnerOnly';
import StyledCheckbox from '../StyledCheckbox';
import { CSelect } from './CSelect';
import { IFiltersV2Props } from './FiltersV2.interface';
import { FilterSelectPlaceholder } from './FiltersV2.placeholder';
import { StyledPopover, useStyles } from './FiltersV2.styles';

const isIncluded = ({ filters, value }: any) =>
  Array.isArray(value) ? !!intersection(filters, value).length : filters.includes(value);

const listReducerStrategy = new Map<string, any>([
  ['publication_date', reducePublicationDateCheckbox],
  ['release_date', reducePublicationDateCheckbox],
]);

const getFacetOptionValues = (facetName: string, facets: any[]) =>
  pipe(
    find((facetItem: any) => facetItem.name === facetName),
    get('options'),
    map((facetOption: any) => facetOption.value),
  )(facets);

const defaultRenderLabel = ({
  item,
}: {
  item: { value: string; count: number | string; label?: string };
}) => (
  <Typography variant="body2">
    {item?.label ?? item.value} {item.count ? `(${item.count})` : ''}
  </Typography>
);

const FiltersV2 = ({
  renderLabel = defaultRenderLabel,
  onChange = () => null,
  isSorted = false,
  onClose = () => null,
}: IFiltersV2Props) => {
  const styles = useStyles({});
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);
  const [menuItems, setMenuItems] = React.useState<any[]>([]);
  const [selectedName, setSelectedName] = React.useState('');
  const {
    facets: elasticFacets,
    forceUpdate = () => null,
    labels = {},
    queryParams,
    updateQueryParams,
  } = React.useContext(ElasticComponentContext);
  const facets = useProcessFacets(elasticFacets);
  const { isPartnerOnlyFilter, facetHasPartnerOnlyFilters } = usePartnerOnly();
  const { esLabels = {} } = usePageModels();

  const handleClose = React.useCallback(() => {
    setAnchorEl(null);
  }, []);
  const handleClick = React.useCallback(
    (name = '') =>
      (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event?.currentTarget);
        setSelectedName(name);
      },
    [],
  );

  const buildFilterMenu = React.useCallback(() => {
    const filtersWithOptions = facets.filter((filter: any) => filter.options.length > 0);
    return filtersWithOptions.map((filter: any) => ({
      ...filter,
      options: isSorted
        ? orderBy(filter.options, [({ value }) => value?.toLowerCase()], ['asc'])
        : filter.options,
    }));
  }, [facets, isSorted]);

  const handleApplyFilters = () => {
    forceUpdate();
    onClose();
    handleClose();
  };

  useDeepCompareEffect(() => {
    const sortedOptions = facets.length > 0 ? buildFilterMenu() : [];
    setMenuItems(sortedOptions);
  }, [facets, isSorted]);

  const selectedOptions = React.useMemo(
    () => menuItems?.find(({ name }) => name === selectedName)?.options || [],
    [menuItems, selectedName],
  );

  const [filteredFilters, setFilteredFilters] = React.useState<any[]>([]);

  const handleChange = (value: string | string[]) => {
    const allFilters = queryParams.filters[selectedName] || [];
    let args: any[] = [];

    if (isIncluded({ filters: allFilters, value })) {
      args = allFilters.filter((tag: string) => {
        return Array.isArray(value) ? !value.includes(tag) : tag !== value;
      });
    } else {
      args = Array.isArray(value) ? [...allFilters, ...value] : [...allFilters, value];
    }

    updateQueryParams(selectedName, args, true);
    onChange();
  };

  React.useEffect(() => {
    if (!selectedOptions.length) return;

    const list = selectedOptions.filter((item: any) => item.value.length);
    const listReduced = listReducerStrategy.has(selectedName)
      ? listReducerStrategy.get(selectedName)({ list })
      : reduceCheckboxDefault({ list, labels: esLabels });

    setFilteredFilters(listReduced.filter(({ value }: any) => value !== 'null'));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOptions]);

  const isCMSPreview = IS_PREVIEW && menuItems.length < 0;

  return (
    <div className={styles.container}>
      {isCMSPreview && <FilterSelectPlaceholder itemCount={4} theme={Theme.dark} />}
      {menuItems.map((menuItem, idx: number) => (
        <CSelect
          key={menuItem.name}
          handleClick={handleClick(menuItem.name)}
          hasAnchorEl={!!anchorEl}
          label={menuItem?.label}
        />
      ))}
      <StyledPopover
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        disableScrollLock
      >
        {/* <Typography variant="body2" className={styles.itemsTitle}>
          Partner Only
        </Typography> */}
        <div className={styles.items}>
          {facetHasPartnerOnlyFilters(
            selectedName,
            getFacetOptionValues(selectedName, menuItems),
          ) && (
            <div className={styles.partnerOnlyWrapper}>
              <PartnerOnly theme={Theme.dark} />
            </div>
          )}
          {filteredFilters?.map((filter: any, index: number) => (
            <div key={`${filter.value}_${index.toString()}`} className={styles.item}>
              <MuiFormGroup>
                <MuiFormControlLabel
                  control={
                    <StyledCheckbox
                      checked={
                        queryParams.filters[selectedName]
                          ? isIncluded({
                              filters: queryParams.filters[selectedName],
                              value: filter.value,
                            })
                          : false
                      }
                      onChange={() => handleChange(filter.value)}
                      name={filter?.label || filter.value}
                      color="secondary"
                      theme={Theme.dark}
                    />
                  }
                  label={
                    <div className={styles.filterLabelWrapper}>
                      {renderLabel({ selectedName, item: filter })}
                      {isPartnerOnlyFilter(selectedName, filter.value) && (
                        <PartnerOnly
                          theme={Theme.dark}
                          onlyIcon
                          className={styles.labelPartnerOnlyIcon}
                        />
                      )}
                    </div>
                  }
                />
              </MuiFormGroup>
            </div>
          ))}
        </div>
        <Button theme={Theme.dark} outlined variant="button" fullWidth onClick={handleApplyFilters}>
          {labels?.close_apply_filters || 'Close & Apply Filters'}
        </Button>
      </StyledPopover>
    </div>
  );
};

export default FiltersV2;
